import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { Team, Zone, Player } from '../../types';
import { formatPairName } from '../../lib/utils';
import { SearchableTeamSelect } from '../common/SearchableTeamSelect';
import { PairAvatar } from '../common/PairAvatar';

interface AdminTournamentZoneProps {
  key?: any;
  tId: string;
  tZones: Zone[];
  zone: Zone;
  zIdx: number;
  allTeams: Team[];
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  hasPlayoffs?: boolean;
  qualifiersPerZone?: number;
}

export function AdminTournamentZone({
  tId,
  tZones,
  zone,
  zIdx,
  allTeams,
  players,
  db,
  getCollectionPath,
  hasPlayoffs,
  qualifiersPerZone,
  onDropGlobal,
}: AdminTournamentZoneProps & { onDropGlobal?: any }) {

  const handleTouchStart = (e: React.TouchEvent, teamId: string | null, teamName: string) => {
    if (!teamId) return;

    const initialTouch = e.touches[0];
    const startX = initialTouch.clientX;
    const startY = initialTouch.clientY;
    
    let localIsDragging = false;
    let dragAuthorized = false; // Will be true after 300ms hold
    let ghost: HTMLDivElement | null = null;
    const sourceInfo = { type: 'zone', zIdx };

    const longPressTimer = setTimeout(() => {
      dragAuthorized = true;
    }, 300);
    
    const onMove = (moveEv: TouchEvent) => {
      const t = moveEv.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If we haven't authorized drag (300ms) but user moved significantly, it's a scroll
      if (!dragAuthorized && distance > 10) {
        clearTimeout(longPressTimer);
        return;
      }

      if (dragAuthorized && !localIsDragging && distance > 5) {
        localIsDragging = true;
        
        ghost = document.createElement('div');
        ghost.id = 'drag-ghost-mobile-zone';
        ghost.innerHTML = `<div class="flex items-center theme-primary-bg text-black px-4 py-2 rounded-full text-[12px] font-black shadow-lg border-2 border-white pointer-events-none whitespace-nowrap"><span class="mr-2 opacity-50 italic">MOVIENDO...</span>${teamName}</div>`;
        ghost.style.position = 'fixed';
        ghost.style.zIndex = '999999';
        ghost.style.pointerEvents = 'none';
        ghost.style.transform = 'translate(-50%, -150%)';
        ghost.style.left = `${t.clientX}px`;
        ghost.style.top = `${t.clientY}px`;
        document.body.appendChild(ghost);
      }

      if (localIsDragging) {
        if (moveEv.cancelable) moveEv.preventDefault();
        
        if (ghost) {
          ghost.style.left = `${t.clientX}px`;
          ghost.style.top = `${t.clientY}px`;
          ghost.style.visibility = 'hidden';
        }
        
        const elUnderPoint = document.elementFromPoint(t.clientX, t.clientY);
        if (ghost) ghost.style.visibility = 'visible';

        const found = elUnderPoint?.closest('[data-drop-slot]');
        document.querySelectorAll('.touch-drop-target').forEach(el => el.classList.remove('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50'));
        if (found) found.classList.add('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50');
      }
    };

    const onEnd = () => {
      clearTimeout(longPressTimer);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      
      if (localIsDragging) {
        const finalTarget = document.querySelector('.touch-drop-target');
        
        if (finalTarget && onDropGlobal) {
          const targetSlot = parseInt(finalTarget.getAttribute('data-drop-slot') || '1') as 1 | 2;
          const targetBIdx = parseInt(finalTarget.getAttribute('data-bidx') || '0');
          const targetMIdx = parseInt(finalTarget.getAttribute('data-midx') || '0');
          onDropGlobal(targetBIdx, targetMIdx, teamId, teamName, targetSlot, sourceInfo);
        }

        document.querySelectorAll('.touch-drop-target').forEach(el => el.classList.remove('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50'));
        if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
      }
    };

    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: false });
    window.addEventListener('touchcancel', onEnd, { passive: false });
  };

  const updateTeamStats = async (tIdx: number, field: string, val: string) => {
    const updatedZones = [...tZones];
    (updatedZones[zIdx].teams[tIdx] as any)[field] = parseInt(val) || 0;
    await updateDoc(doc(db, getCollectionPath('tournaments'), tId), { zones: updatedZones });
  };

  return (
    <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden shadow-2xl group/zone">
      <div className="bg-gradient-to-r from-white/10 to-transparent px-4 py-3 flex justify-between items-center border-b border-white/5">
        <h4 className="text-sm md:text-base font-black uppercase tracking-widest theme-font-secondary opacity-90">{zone.name}</h4>
        <div className="flex gap-1">
          <button
            onClick={() => {
              sessionStorage.setItem('pendingZoneResult', JSON.stringify({ tournamentId: tId, zoneName: zone.name }));
              window.dispatchEvent(new CustomEvent('changeAdminTab', { detail: { tab: 'resultados' } }));
              window.dispatchEvent(new CustomEvent('navigateToResultados'));
            }}
            className="flex items-center gap-1.5 px-3 py-1 bg-white/5 hover:bg-white/10 text-[9px] font-black uppercase text-white/70 hover:text-white rounded-lg transition-colors border border-white/10 active:scale-95"
          >
            Ver partidos
          </button>
        </div>
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full text-[10px] md:text-xs text-center border-separate border-spacing-0">
          <thead className="bg-black/40 text-[8px] md:text-[9px] font-black uppercase tracking-tighter opacity-60">
            <tr>
              <th className="py-2 px-3 text-left">Pareja</th>
              <th className="py-2 px-1 theme-primary-text">PTS</th>
              <th className="py-2 px-1">PJ</th>
              <th className="py-2 px-1">PG</th>
              <th className="py-2 px-1">PP</th>
              <th className="py-2 px-1">SF</th>
              <th className="py-2 px-1">SC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(() => {
              const sortedTeams = [...zone.teams].sort((a, b) => {
                if (b.pts !== a.pts) return b.pts - a.pts;
                const diffB = (b.sf || 0) - (b.sc || 0);
                const diffA = (a.sf || 0) - (a.sc || 0);
                if (diffB !== diffA) return diffB - diffA;
                return (b.sf || 0) - (a.sf || 0);
              });

              return sortedTeams.map((team, i) => {
                const originalIdx = zone.teams.findIndex(t => t.teamId === team.teamId);
                const isQualifying = hasPlayoffs && qualifiersPerZone && (i < qualifiersPerZone);
                const pp = (team as any).pp !== undefined ? (team as any).pp : ((team.pj || 0) - (team.pg || 0));

                const tInfo = (() => {
                  if (team.teamId) {
                    const found = allTeams.find((tm) => tm.id === team.teamId);
                    if (found) {
                      const p1 = players.find(p => p.id === found.player1Id);
                      const p2 = players.find(p => p.id === found.player2Id);
                      return { 
                        p1Id: found.player1Id,
                        p2Id: found.player2Id,
                        p1Photo: p1?.photoUrl,
                        p2Photo: p2?.photoUrl,
                        photoUrl: found.photoUrl
                      };
                    }
                  }
                  return { p1Id: null, p2Id: null, p1Photo: null, p2Photo: null, photoUrl: null };
                })();

                return (
                  <tr 
                    key={i} 
                    draggable={!!team.teamId}
                    onDragStart={(e) => {
                      if (team.teamId) {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', JSON.stringify({
                          teamId: team.teamId,
                          teamName: team.name,
                          sourceInfo: { type: 'zone', zIdx }
                        }));
                      }
                    }}
                    onTouchStart={(e) => handleTouchStart(e, team.teamId, team.name)}
                    className={`hover:bg-white/5 transition-all relative group/row ${isQualifying ? 'bg-theme-primary/[0.04]' : (i % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent')} ${team.teamId ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  >
                    <td className="py-1.5 px-3 text-left font-bold relative min-w-[150px] md:min-w-[180px]">
                      <style dangerouslySetInnerHTML={{__html: `
                        @media (pointer: coarse) {
                          tr[draggable="true"] {
                            -webkit-touch-callout: none;
                            -webkit-user-drag: none;
                            user-select: none;
                          }
                        }
                      `}} />
                      {isQualifying && (
                        <div className="absolute left-0 top-1 bottom-1 w-0.5 theme-primary-bg shadow-[0_0_10px_rgba(var(--glow-rgb),0.5)]"></div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className={`w-3 text-[8px] md:text-[9px] ${isQualifying ? 'theme-primary-text font-black' : 'opacity-20'}`}>
                          {i + 1}
                        </span>
                        
                        <div className="shrink-0 scale-[0.65] md:scale-[0.75] -mx-1.5 md:-mx-1">
                          <PairAvatar 
                            p1Id={tInfo.p1Id}
                            p2Id={tInfo.p2Id}
                            p1Image={tInfo.p1Photo || tInfo.photoUrl}
                            p2Image={tInfo.p2Photo}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className={`w-full bg-transparent font-black tracking-tight text-[9px] md:text-[11px] uppercase truncate ${isQualifying ? 'theme-primary-text' : 'text-slate-200'}`}>
                            {team.teamId ? formatPairName(team.name, 'short') : 'LIBRE...'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={team.pts}
                        onChange={(e) => updateTeamStats(originalIdx, 'pts', e.target.value)}
                        className="w-8 bg-black/40 border border-theme-primary/20 text-center theme-primary-text text-[10px] md:text-xs font-black rounded py-1 outline-none focus:border-theme-primary/50 transition-colors"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={team.pj}
                        onChange={(e) => updateTeamStats(originalIdx, 'pj', e.target.value)}
                        className="w-7 bg-black/20 border border-white/5 text-center text-[9px] md:text-[10px] font-bold rounded py-0.5 outline-none focus:border-white/20 transition-colors opacity-60 hover:opacity-100"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={team.pg}
                        onChange={(e) => updateTeamStats(originalIdx, 'pg', e.target.value)}
                        className="w-7 bg-black/20 border border-white/5 text-center text-[9px] md:text-[10px] font-bold rounded py-0.5 outline-none focus:border-white/20 transition-colors opacity-60 hover:opacity-100"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={pp}
                        onChange={(e) => {
                          // Note: PP doesn't have its own field in the model, usually derived as pj - pg.
                          // But if we want it editable we might need to adjust pj or pg? 
                          // Or we add pp to the zone team object. Wait, let's see how updateTeamStats works.
                          updateTeamStats(originalIdx, 'pp', e.target.value);
                        }}
                        className="w-7 bg-black/20 border border-white/5 text-center text-[9px] md:text-[10px] font-bold rounded py-0.5 outline-none focus:border-white/20 transition-colors opacity-60 hover:opacity-100"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={team.sf || 0}
                        onChange={(e) => updateTeamStats(originalIdx, 'sf', e.target.value)}
                        className="w-7 bg-black/20 border border-white/5 text-center text-[9px] md:text-[10px] font-bold rounded py-0.5 outline-none focus:border-white/20 transition-colors opacity-60 hover:opacity-100"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        value={team.sc || 0}
                        onChange={(e) => updateTeamStats(originalIdx, 'sc', e.target.value)}
                        className="w-7 bg-black/20 border border-white/5 text-center text-[9px] md:text-[10px] font-bold rounded py-0.5 outline-none focus:border-white/20 transition-colors opacity-60 hover:opacity-100"
                      />
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
